import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  CheckoutState,
  StateControlParameters,
  StateService,
} from 'src/app/service/state/state.service';
import { CurrencyCode } from '@app/model/currency';
import { formatNumber } from '@angular/common';
import { RouteComponent } from '@app/route/route.component';
import { QueryParamsService } from '@app/service/query-params/query-params.service';
import { Subscription } from 'rxjs/internal/Subscription';
import { HeaderService } from '@app/service/header/header.service';

@Component({
  selector: 'app-de-precheck',
  templateUrl: './de-precheck.component.html',
  styleUrls: ['../../page-shared.css', './de-precheck.component.scss'],
})
export class DePrecheckComponent
  extends RouteComponent
  implements OnInit, OnDestroy
{
  loading = true;
  form: FormGroup;
  formSubscription: Subscription;
  fontSize: number = 100;
  showError = false;
  maxLength = 6;
  state: CheckoutState;
  navigationInProgress = false;
  containerWidth: number;
  inputWidth: number;

  CurrencyCode = CurrencyCode;

  constructor(
    protected router: Router,
    protected route: ActivatedRoute,
    protected stateService: StateService,
    protected headerService: HeaderService,
    private fb: FormBuilder,
    protected queryParamsService: QueryParamsService
  ) {
    super(router, route, stateService, headerService);
  }

  @ViewChild('amountInput') amountInput: ElementRef<HTMLInputElement>;

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    if (this.loading) {
      return;
    }
    this.calcFontSize();
  }

  async ngOnInit(): Promise<void> {
    if (this.state.forceNavigation) {
      const route = this.stateService.getRouteFromState(this.state);
      this.router.navigate([route], { queryParamsHandling: 'preserve' });
    }

    const useTotalCheckoutValue: boolean = this.state.totalCheckoutValue != null;

    const maxValue: number = useTotalCheckoutValue
        ? this.state.totalCheckoutValue.amount
        : this.state.checkoutCustomizations.merchantPrecheckMaxAmount;

    this.form = this.fb.group({
      amount: [
        this.formatDEAmount(
          this.state.totalCheckoutValue && this.state.totalCheckoutValue.amount
        ),
        [precheckAmountValidator(1, maxValue), Validators.required],
      ],
    });

    this.calcInputWidth();

    this.formSubscription = this.form.valueChanges.subscribe((form) => {
      this.showError = false;
      const onlyNumbers = form.amount.replace(/\D/g, '');
      this.form.patchValue(
        {
          amount: this.formatDEAmount(onlyNumbers),
        },
        { emitEvent: false }
      );

      this.calcFontSize();
      this.calcInputWidth();
    });

    if (this.form.value.amount) {
      this.calcFontSize();
    }

    this.loading = false;
  }

  ngOnDestroy() {
    this.formSubscription.unsubscribe();
  }

  formatDEAmount(amount): string {
    return formatNumber(amount || 0, 'de');
  }

  calcInputWidth(): void {
    // Count dots and commas as 0.5 units since they use less width
    const dotsAndCommas = this.form.value.amount
      .split('')
      .filter((item: string) => item === '.' || item === ',');

    this.inputWidth =
      this.form.value.amount.toString().length - dotsAndCommas.length * 0.5;
  }

  calcFontSize(): void {
    this.fontSize = Math.min(screen.width / this.form.value.amount.length, 100);
  }

  async handleCtaClick(): Promise<void> {
    if (currrencyStringToNumber(this.form.controls.amount.value) === 0) {
      this.amountInput.nativeElement.focus();
    }

    if (this.form.controls.amount.invalid) {
      this.showError = true;
      return;
    }

    this.showError = true;
    this.navigationInProgress = true;

    try {
      await this.stateService.setValue({
        amount: currrencyStringToNumber(this.form.value.amount),
        currencyCode: CurrencyCode.EUR,
      });
      this.navigate();
    } catch (err) {
      this.handleError(err);
    }

    this.navigationInProgress = false;
  }
}

export const currrencyStringToNumber = (value: string | number): number => {
  return parseInt(value.toString().replace(/\D/g, ''));
};

export const precheckAmountValidator = (
  min: number,
  max: number
): ValidatorFn => {
  return (control: AbstractControl): { [key: string]: boolean } | null => {
    const value = currrencyStringToNumber(control.value);

    if (isNaN(value) || value < min || value > max) {
      return { ageRange: true };
    }
    return null;
  };
};
